import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
// import { AppState } from '@capacitor/app';
import { setLoadingSpinner } from '../actions/shared.action';
import { catchError, exhaustMap, filter, map, mergeMap, of, tap, withLatestFrom } from 'rxjs';
import * as fromAccountActions from '././../actions/account.action'
import { APIResponse } from 'src/app/models/api-response.model';
import { AccountService } from 'src/app/services/account.service';
import { OfflineStorageService } from 'src/app/services/offline-storage.service';
import { AppState } from '../app.state';
import { ModalService } from 'src/app/services/modal.service';
import { Router } from '@angular/router';
import { getUserProfile } from '../selectors/account.selector';
import { AuthService } from 'src/app/services/auth.service';
import { ROUTER_NAVIGATION, RouterNavigatedAction } from '@ngrx/router-store';

@Injectable()
export class AccountEffects {


  getUserProfile$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getUserProfile),
      exhaustMap((action) =>
        this.accountService.userProfile().pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.getUserProfileSuccess({ data: response?.details })
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromAccountActions.getUserProfileFailed({ data: response?.error?.message }))
          })
        )
      )
    )
  );

  getUserProfileSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getUserProfileSuccess),
      tap(async (action) => {
        await this.offlineStorageService.setObject('user', JSON.stringify(action.data));

        if (action.data?.userType !== 'employee') {
          this.store.dispatch(fromAccountActions.getStudentApplication({ redirect: true }));
        } else {
          // Dismiss spinner before navigating to avoid stale UI state
          this.store.dispatch(setLoadingSpinner({ status: false }));
          this.router.navigate(['/home/dashboard']);
        }
      })
    ),
    { dispatch: false }
  );

  getUserProfileFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getUserProfileFailed),
      tap(async (action) => {
        // if(action.data){
        //   const message = action.data;
        //   this.modalService.errorModal(message)
        // } else{
        //   const message = 'Unkown error ocurred. Please try again later.'
        //   this.modalService.errorModal(message)
        // }
      })
    ),
    { dispatch: false }
  );

  getStudentApplication$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getStudentApplication),
      withLatestFrom(this.store.select(getUserProfile), (action, user) => {
        return { ...action, user };
      }),
      exhaustMap((action) =>
        this.accountService.getStudentApplication().pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));

            const data = {
              details: response?.details,
              user: action.user
            }
            return fromAccountActions.getStudentApplicationSuccess({ data, redirect: action?.redirect })
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromAccountActions.getStudentApplicationFailed({ data: response?.error?.message }))
          })
        )
      )
    )
  );

  getStudentApplicationSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getStudentApplicationSuccess),
      tap(async (action) => {

        if (action?.redirect) {
          this.store.dispatch(setLoadingSpinner({ status: false }));

          if (!action.data?.details) {
            // No application on file yet
            if (!action?.data?.user?.student) {
              // No student record → send to welcome or dashboard depending on userType
              const route = action?.data?.user?.userType ? '/student/dashboard' : '/student/welcome';
              this.router.navigateByUrl(route);
            } else {
              // Has student record but no application → check enrollment status
              this.store.dispatch(fromAccountActions.getLatestEnrollmentStatus());
            }
          } else {
            const notes = action?.data?.details?.notes;
            const latestNote = this.accountService.getLatestNote(notes);
            const status = latestNote?.status;

            // Dispatch extra data load for passed first interview before routing
            if (status === 'first_interview_scheduled_passed') {
              this.store.dispatch(fromAccountActions.getAcademicRequirements({ data: action?.data?.details?.admission_type }));
            }
            
            // Let checkStatus decide the destination for all statuses (including 'complete')
            this.accountService.checkStatus(notes);
          }
        } else {
          // No redirect needed — still load academic requirements if relevant
          const notes = action?.data?.details?.notes;
          const latestNote = this.accountService.getLatestNote(notes);
          const status = latestNote?.status;

          if (status === 'first_interview_scheduled_passed') {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            this.store.dispatch(fromAccountActions.getAcademicRequirements({ data: action?.data?.details?.admission_type }));
          }
        }
      })
    ),
    { dispatch: false }
  );

  getStudentApplicationFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getStudentApplicationFailed),
      tap(async (action) => {
        if (action.data) {
          const message = action.data;
          this.modalService.errorModal(message)
        } else {
          const message = 'Unkown error ocurred. Please try again later.'
          this.modalService.errorModal(message)
        }
      })
    ),
    { dispatch: false }
  );

  getPreEnrollmentRequirements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r: RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/student/pre-enrollment');
      }),
      exhaustMap((action) =>
        this.accountService.getPreEnrollmentRequirements().pipe(
          map((response) => {
            return fromAccountActions.getPreEnrollmentRequirementsSuccess({ data: response });
          }),
          catchError((response) => {
            return of(fromAccountActions.getPreEnrollmentRequirementsFailed({ data: response.error }));
          })
        )
      )
    )
  });

  getPreEnrollmentRequirementsSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getPreEnrollmentRequirementsSuccess),
      tap(async (action) => {
        console.log("getPreEnrollmentRequirementsSuccess$", action.data);
      })
    ),
    { dispatch: false }
  );

  getPreEnrollmentRequirementsFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getPreEnrollmentRequirementsFailed),
      tap(async (action) => {

        if (action.data) {
          const message = action.data;
          this.modalService.errorModal(message)
        } else {
          const message = 'Unkown error ocurred. Please try again later.'
          this.modalService.errorModal(message)
        }
      })
    ),
    { dispatch: false }
  );

  getAcademicRequirements$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getAcademicRequirements),
      exhaustMap((action) =>
        this.accountService.getAcademicRequirements(action?.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.getAcademicRequirementsSuccess({ data: response })
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromAccountActions.getAcademicRequirementsFailed({ data: response?.error?.message }))
          })
        )
      )
    )
  );

  getAcademicRequirementsSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getAcademicRequirementsSuccess),
      tap(async (action) => {
        console.log("getAcademicRequirementsSuccess$", action.data);
      })
    ),
    { dispatch: false }
  );

  getAcademicRequirementsFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getAcademicRequirementsFailed),
      tap(async (action) => {
        console.log("getAcademicRequirementsFailed$", action.data);
      })
    ),
    { dispatch: false }
  );

  reserveEnrollment$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.reserveEnrollment),
      exhaustMap((action) =>
        this.accountService.submitEnrollment().pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.reserveEnrollmentSuccess({ data: response })
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromAccountActions.reserveEnrollmentFailed({ data: response?.error?.message }))
          })
        )
      )
    )
  );

  reserveEnrollmentSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.reserveEnrollmentSuccess),
      tap(async (action) => {
        this.store.dispatch(fromAccountActions.getUserProfile());
        this.router.navigateByUrl('/student/dashboard')
        this.modalService.successModal(action.data)
      })
    ),
    { dispatch: false }
  );

  reserveEnrollmentFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.reserveEnrollmentFailed),
      tap(async (action) => {
        if (action.data) {
          const message = action.data;
          this.modalService.errorModal(message)
        } else {
          const message = 'Unkown error ocurred. Please try again later.'
          this.modalService.errorModal(message)
        }
      })
    ),
    { dispatch: false }
  );

  submitPreEnlistment$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.submitPreEnlistment),
      exhaustMap((action) =>
        this.accountService.submitPreEnlistment(action?.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.submitPreEnlistmentSuccess({ data: response })
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromAccountActions.submitPreEnlistmentFailed({ data: response?.error?.message }))
          })
        )
      )
    )
  );

  submitPreEnlistmentSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.submitPreEnlistmentSuccess),
      tap(async (action) => {
        this.store.dispatch(fromAccountActions.getUserProfile());
        this.modalService.successModal(action.data)
      })
    ),
    { dispatch: false }
  );

  submitPreEnlistmentFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.submitPreEnlistmentFailed),
      tap(async (action) => {
        if (action.data) {
          const message = action.data;
          this.modalService.errorModal(message)
        } else {
          const message = 'Unkown error ocurred. Please try again later.'
          this.modalService.errorModal(message)
        }
      })
    ),
    { dispatch: false }
  );

  getLatestEnrollmentStatus$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getLatestEnrollmentStatus),
      exhaustMap((action) =>
        this.accountService.getLatestEnrollmentStatus().pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.getLatestEnrollmentStatusSuccess({ data: response?.details })
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromAccountActions.getLatestEnrollmentStatusFailed({ data: response?.error?.message }))
          })
        )
      )
    )
  );

  getLatestEnrollmentStatusSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getLatestEnrollmentStatusSuccess),
      withLatestFrom(this.store.select(getUserProfile)),
      tap(async ([action, userProfile]) => {
        this.store.dispatch(setLoadingSpinner({ status: false }));
        console.log('getLatestEnrollmentStatusSuccess$', action?.data);
        
        const pre_enlistment = action?.data?.pre_enlistments;
        const admission_types = ['F', 'T', 'D', 'SH'];
        const admission_type = userProfile?.student?.admission_type;
        const year_level = userProfile?.student?.year_level;

        if (pre_enlistment) {
          if (pre_enlistment?.preenlistment_status == 'submitted') {
            this.router.navigateByUrl('/student/undergraduate-clearance');
          } else if (pre_enlistment?.preenlistment_status == 'blocked') {
            this.router.navigateByUrl('/student/undergraduate-clearance');
          } else if (pre_enlistment?.preenlistment_status == 'cleared') {
            this.router.navigateByUrl('/student/undergraduate-clearance');
          } else {
            this.router.navigateByUrl('/student/welcome');
          }
        } else if (!pre_enlistment && admission_type && !admission_types.includes(admission_type)) {
          this.router.navigateByUrl('/student/undergraduate-form');
        } else if (!pre_enlistment && admission_type && admission_types.includes(admission_type) && year_level !== 1) {
          this.router.navigateByUrl('/student/undergraduate-form');
        } else {
          this.router.navigateByUrl('/student/welcome');
        }
      })
    ),
    { dispatch: false }
  );

  getLatestEnrollmentStatusFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getLatestEnrollmentStatusFailed),
      tap(async (action) => {
        if (action.data) {
          const message = action.data;
          this.modalService.errorModal(message)
        } else {
          const message = 'Unkown error ocurred. Please try again later.'
          this.modalService.errorModal(message)
        }
      })
    ),
    { dispatch: false }
  );

  getCurriculumCompletion$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getCurriculumCompletion),
      exhaustMap((action) =>
        this.accountService.getCurriculumCompletion(action?.data).pipe(
          map((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return fromAccountActions.getCurriculumCompletionSuccess({ data: response?.details })
          }),
          catchError((response) => {
            this.store.dispatch(setLoadingSpinner({ status: false }));
            return of(fromAccountActions.getCurriculumCompletionFailed({ data: response?.error?.message }))
          })
        )
      )
    )
  );

  getCurriculumCompletionSuccess$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getCurriculumCompletionSuccess),
      tap(async (action) => {
        this.store.dispatch(setLoadingSpinner({ status: false }));
        this.router.navigateByUrl('/student/undergraduate-ccr')
      })
    ),
    { dispatch: false }
  );

  getCurriculumCompletionFailed$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromAccountActions.getCurriculumCompletionFailed),
      tap(async (action) => {
        if (action.data) {
          const message = action.data;
          this.modalService.errorModal(message)
        } else {
          const message = 'Unkown error ocurred. Please try again later.'
          this.modalService.errorModal(message)
        }
      })
    ),
    { dispatch: false }
  );


  constructor(
    private actions$: Actions,
    private accountService: AccountService,
    private offlineStorageService: OfflineStorageService,
    private store: Store<AppState>,
    private modalService: ModalService,
    private router: Router,
    private authService: AuthService
  ) { }

}
